import { useMemo } from 'react';

import { useGenericSearch } from '@/hooks/useGenericSearch';
import { isJobActive, isJobFailed } from '@/sections/jobs/utils/jobStatus.util';
import { Job, JobStatus, ResourceType } from '@/types';
import { getJobStatus } from '@/utils/job';

// Source stamped on the recorder agent's job (backend recorder.AgentName).
const RECORDER_SOURCE = 'recorder-agent';
// Job-config keys the access broker writes when spawning the recorder
// (backend: agentLib.SubagentConversationIDConfigKey, recorder.payloadConfigKey).
const CONVERSATION_ID_KEY = 'subagent_conversation_id';
const PAYLOAD_KEY = 'recorder:payload';

export interface RecorderJob {
  credentialId: string;
  conversationId: string;
  label: string;
  status: JobStatus;
  isActive: boolean;
  isFailed: boolean; // JF — covers a hard failure AND a blocked give-up (both fail the job)
}

// Maps each web-auth credential to its latest recorder-agent run for an asset.
// The recorder job — keyed #job#<group>#<identifier>#recorder-agent and carrying
// the credential id + conversation in its config — is the single source of truth
// for recording status: a blocked give-up fails the job too (finalize blocked:true
// → fatal), so job status alone settles recorded-vs-failed.
export function useRecorderJobs(
  group?: string,
  identifier?: string,
  // Only the credential list modal renders the status cells; skip polling when
  // it's closed (CredentialModals stays mounted per-asset, so this hook would
  // otherwise poll every 5s in the background for no consumer).
  enabled = true
): { byCredential: Map<string, RecorderJob> } {
  const key =
    group && identifier
      ? `#job#${group}#${identifier}#${RECORDER_SOURCE}`
      : undefined;

  const { data } = useGenericSearch(
    { label: ResourceType.Job, query: key, desc: true },
    { enabled: Boolean(key) && enabled, refetchInterval: 5000 }
  );

  const byCredential = useMemo(() => {
    const map = new Map<string, RecorderJob>();
    for (const job of data?.jobs ?? []) {
      const config = (job.config ?? {}) as Record<string, string>;
      const conversationId = config[CONVERSATION_ID_KEY];
      const payload = readPayload(config);
      if (!conversationId || !payload.credentialId) continue;
      // desc by updated → first seen per credential is the most recent run.
      if (map.has(payload.credentialId)) continue;
      const status = getJobStatus(job as Job);
      map.set(payload.credentialId, {
        credentialId: payload.credentialId,
        conversationId,
        label: payload.label || 'this login',
        status,
        isActive: isJobActive(status),
        isFailed: isJobFailed(status),
      });
    }
    return map;
  }, [data]);

  return { byCredential };
}

function readPayload(config: Record<string, string>): {
  label: string;
  credentialId: string;
} {
  try {
    const p = JSON.parse(config[PAYLOAD_KEY] ?? '{}');
    return {
      label: typeof p.label === 'string' ? p.label : '',
      credentialId: typeof p.credential_id === 'string' ? p.credential_id : '',
    };
  } catch {
    return { label: '', credentialId: '' };
  }
}
